import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CalendarClock, Info } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const Row = ({ it, active }) => {
  const qc = useQueryClient();
  const [f, setF] = useState({ opname_van: it.opname_van || "", opname_tot: it.opname_tot || "", beschikbaar_sinds: it.beschikbaar_sinds || "", notitie: it.notitie || "" });
  const dirty = ["opname_van", "opname_tot", "beschikbaar_sinds", "notitie"].some((k) => (it[k] || "") !== f[k]);
  const save = async () => {
    try {
      await api.put(`/luchtfotos/${it.year}`, { ...f, beschikbaar_sinds: f.beschikbaar_sinds || null });
      toast.success(`Opnamegegevens ${it.year} opgeslagen`);
      qc.invalidateQueries({ queryKey: ["luchtfotos"] });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const inp = "h-8 rounded bg-slate-950 border border-slate-800 px-2 text-xs font-mono w-full";
  return (
    <tr className="border-b border-slate-800/60" data-testid={`imagery-row-${it.year}`}>
      <td className="px-3 py-2 font-mono font-medium">{it.year}{active && <span className="ml-2 text-[10px] text-amber-400">{active}</span>}</td>
      <td className="px-3 py-2 font-mono text-xs text-slate-400">{it.resolutie}</td>
      <td className="px-3 py-2"><input type="date" className={inp} value={f.opname_van} onChange={(e) => setF({ ...f, opname_van: e.target.value })} data-testid={`imagery-from-${it.year}`} /></td>
      <td className="px-3 py-2"><input type="date" className={inp} value={f.opname_tot} onChange={(e) => setF({ ...f, opname_tot: e.target.value })} data-testid={`imagery-to-${it.year}`} /></td>
      <td className="px-3 py-2"><input type="date" className={inp} value={f.beschikbaar_sinds} onChange={(e) => setF({ ...f, beschikbaar_sinds: e.target.value })} data-testid={`imagery-available-${it.year}`} /></td>
      <td className="px-3 py-2 min-w-[200px]"><input className={inp} value={f.notitie} onChange={(e) => setF({ ...f, notitie: e.target.value })} data-testid={`imagery-note-${it.year}`} /></td>
      <td className="px-3 py-2">{dirty && <button onClick={save} data-testid={`imagery-save-${it.year}`} className="text-xs px-2 py-1 rounded bg-blue-600 hover:bg-blue-500">Opslaan</button>}</td>
    </tr>
  );
};

export const ImageryCatalog = ({ settings }) => {
  const { data } = useQuery({ queryKey: ["luchtfotos"], queryFn: () => api.get("/luchtfotos").then((r) => r.data) });
  if (!data) return null;
  const label = (y) => (y === settings.baseline_year ? "BASIS" : y === settings.compare_year ? "NIEUW" : null);
  return (
    <div className="border border-slate-800 bg-[#111827]/80 rounded-lg p-6 mt-6" data-testid="imagery-catalog">
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="font-display font-semibold text-lg">Luchtfoto jaargangen</h3>
          <p className="text-xs text-slate-400 mt-1 max-w-xl leading-relaxed">
            <Info className="inline h-3.5 w-3.5 mr-1" />Bron: {data.items[0]?.bron}. Er wordt één landsdekkende, bladloze vlucht per jaar uitgevoerd (meestal februari–april). Nieuwe jaargangen worden automatisch gedetecteerd.
          </p>
        </div>
        <div className="border border-amber-500/30 bg-amber-500/5 rounded-md p-3 text-xs min-w-[240px]" data-testid="next-imagery">
          <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest text-amber-400 uppercase"><CalendarClock className="h-3.5 w-3.5" /> Volgende luchtfoto</div>
          <div className="mt-1.5 font-display text-lg font-bold">{data.volgende.year}</div>
          <div className="text-slate-400">Opname: {data.volgende.verwacht_opname}</div>
          <div className="text-slate-400">Beschikbaar: {data.volgende.verwacht_beschikbaar}</div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left font-mono text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-800">
            <tr><th className="px-3 py-2">Jaar</th><th className="px-3 py-2">Resolutie</th><th className="px-3 py-2">Opname van</th><th className="px-3 py-2">Opname tot</th><th className="px-3 py-2">Beschikbaar sinds</th><th className="px-3 py-2">Notitie</th><th /></tr>
          </thead>
          <tbody>{data.items.map((it) => <Row key={it.year} it={it} active={label(it.year)} />)}</tbody>
        </table>
      </div>
    </div>
  );
};
